import { useEffect, useState } from 'react';

import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { cn } from '@/lib/utils';
import { RangeMode, RangeModeFilter } from '@/types/filters';

interface Props {
  config: RangeModeFilter;
  value: string;
  onChange: (key: string, value: string) => void;
}

export const FilterRangeMode = ({ config, value, onChange }: Props) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<RangeMode | ''>('');
  const [amount, setAmount] = useState('');

  useEffect(() => {
    if (value?.includes(':')) {
      const [mode, amt] = value.split(':');
      setSelected(mode as RangeMode);
      setAmount(amt);
    } else {
      setSelected('');
      setAmount('');
    }
  }, [value]);

  const handleApply = () => {
    if (selected && amount) {
      onChange(config.name, `${selected}:${amount}`);
    }
    setOpen(false);
  };

  const handleClear = () => {
    setSelected('');
    setAmount('');
    onChange(config.name, '');
    setOpen(false);
  };

  const label = value
    ? (() => {
        const [m, a] = value.split(':');
        const label = config.modes.find(opt => opt.value === m)?.label ?? '';
        return `: ${label} ${a}${config.unit ?? ''}`;
      })()
    : '';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        className={cn('h-9 px-2 py-1 border rounded text-sm text-left', open && 'bg-muted')}
      >
        <div className="flex items-center justify-between gap-2">
          <span>
            {config.label}
            <span className="font-medium">{label}</span>
          </span>

          {<ChevronDown className={cn('transition', open && 'rotate-180')} size={16} />}
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-42 space-y-3 p-2" side="bottom" align="start">
        <RadioGroup value={selected} onValueChange={val => setSelected(val as RangeMode)}>
          {config.modes.map(({ label, value: mode }) => (
            <div key={mode} className="flex items-center space-x-2">
              <RadioGroupItem value={mode} id={mode} />
              <label htmlFor={mode} className="flex-1 text-sm">
                {label}
              </label>
              <div className="relative w-24">
                <Input
                  type="number"
                  min={0}
                  max={100}
                  placeholder={config?.placeholder}
                  disabled={selected !== mode}
                  value={selected === mode ? amount : ''}
                  onChange={e => setAmount(e.target.value)}
                  // Input type number arrows remove
                  className="h-8 pr-4 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                />
                {config.unit && (
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                    {config.unit}
                  </span>
                )}
              </div>
            </div>
          ))}
        </RadioGroup>

        <div className="flex justify-between pt-2">
          <Button size="sm" type="button" onClick={handleClear} variant="ghost">
            {t('common:filters.clearAll')}
          </Button>
          <Button size="sm" onClick={handleApply}>
            {t('common:filters.apply')}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
